import { type ReactNode } from 'react';
import ReactSelect from 'react-select';
import type { OptionTypeBase } from 'react-select';
import ReactSelectAsync from 'react-select/async';
import ReactSelectAsyncCreatable from 'react-select/async-creatable';
import ReactSelectCreatable from 'react-select/creatable';

import { SelectType } from './SelectType';
import type { RenderSelectProps } from './types';

type RenderSelectFunction<
  OptionType extends OptionTypeBase,
  IsMulti extends boolean,
  T extends SelectType,
> = (renderSelectProps: RenderSelectProps<OptionType, IsMulti, T>) => ReactNode;

type SelectTypeToRenderFunctionMap<
  OptionType extends OptionTypeBase,
  IsMulti extends boolean,
> = {
  [T in SelectType]: RenderSelectFunction<OptionType, IsMulti, T>;
};

export const getRenderSelectFunction = <
  OptionType extends OptionTypeBase,
  IsMulti extends boolean,
  T extends SelectType,
>(
  selectType: T,
): RenderSelectFunction<OptionType, IsMulti, T> => {
  const selectTypeToRenderFunctionMap: SelectTypeToRenderFunctionMap<
    OptionType,
    IsMulti
  > = {
    [SelectType.STANDARD]: (renderSelectProps) => (
      <ReactSelect {...renderSelectProps.selectProps} />
    ),
    [SelectType.ASYNC]: (renderSelectProps) => (
      <ReactSelectAsync {...renderSelectProps.selectProps} />
    ),
    [SelectType.CREATABLE]: (renderSelectProps) => (
      <ReactSelectCreatable {...renderSelectProps.selectProps} />
    ),
    [SelectType.ASYNC_CREATABLE]: (renderSelectProps) => (
      <ReactSelectAsyncCreatable {...renderSelectProps.selectProps} />
    ),
  };

  const renderFunction = selectTypeToRenderFunctionMap[selectType];

  return renderFunction;
};

/**
 * Attempts to get the input element from the element in a ref callback.
 *
 * A ref passed to a normal react-select Select component will receive the
 * StateManager as its value, but a ref passed to an Async, Creatable, or
 * AsyncCreatable react-select component will receive a wrapper with the
 * StateManager nested in a `select` property. This function provides consistent
 * access to the input element despite these differences.
 */
export const getInputElementFromSelectRefCallbackElement = (
  element: unknown,
): HTMLInputElement | null => {
  /* eslint-disable @typescript-eslint/no-explicit-any */
  // Using any to access properties of unknown.
  const outerInputElement = (element as any)?.select?.inputRef;
  /* eslint-enable @typescript-eslint/no-explicit-any */
  if (outerInputElement instanceof HTMLInputElement) {
    return outerInputElement;
  }

  /* eslint-disable @typescript-eslint/no-explicit-any */
  // Using any to access properties of unknown.
  const innerInputElement = (element as any)?.select?.select?.inputRef;
  /* eslint-enable @typescript-eslint/no-explicit-any */
  if (innerInputElement instanceof HTMLInputElement) {
    return innerInputElement;
  }

  return null;
};
